import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';

import { Layout } from 'components/Layout';
import { PageContent } from 'components/page-content/lotterie-slug/PageContent';
import { PagesPath } from 'types/pages';
import { ParsedUrlQuery } from 'querystring';
import { Seo } from 'components/seo';
import { cleanConfig } from 'lib/datoCms/utils';
import { getAllLotteriesCategoryPage } from 'lib/datoCms/queries/getAllLotteriesCategoryPage';
import { getAllLotteriesCategoryPagesSlug } from 'lib/datoCms/queries/getAllLotteriesCategoryPagesSlug';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { skipStaticPageGeneration } from 'utility/functions';
import { ISR_REVALIDATE } from 'utility/constant';

interface IParams extends ParsedUrlQuery {
  slug: string;
}

const LotteriesSlug = ({
  site,
  footer,
  header,
  needHelp,
  canonical,
  seoMetaTags,
  sidebarInstantGame,
  allMightLikes,
  lotteriesCategoryPages,
}: InferGetStaticPropsType<GetStaticProps>) => {
  return (
    <Layout
      header={header}
      footer={footer}
      needHelp={needHelp}
      mightLikes={allMightLikes}
      sidebarInstantGame={sidebarInstantGame}
    >
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      <PageContent lotteriesPageCategory={lotteriesCategoryPages} />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (skipStaticPageGeneration()) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
  const slugs = await getAllLotteriesCategoryPagesSlug();
  const { allLotteriesCategoryPages: data } = slugs ?? {};

  const pathsArray: PagesPath[] = [];
  data?.map((item) => {
    locales!.map((language) => {
      pathsArray.push({ params: { slug: item.slug }, locale: language });
    });
  });
  const paths = pathsArray;
  return {
    paths: paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  try {
    const { slug } = (params ?? {}) as IParams;

    const {
      site,
      footer,
      header,
      needHelp,
      allLabels: labels,
      sidebarInstantGame,
      config,
    } = await getDatoCMSData('common', locale);

    const prPageData = getAllLotteriesCategoryPage(slug, locale);
    const [pageData] = await Promise.all([prPageData]);

    if (!pageData) {
      return Promise.resolve({ notFound: true });
    }

    const { seoMetaTags, canonical, allMightLikes, ...pageContent } = pageData ?? {};

    return {
      props: {
        site,
        header,
        footer,
        labels,
        config: cleanConfig(config),
        needHelp,
        canonical,
        seoMetaTags,
        sidebarInstantGame,
        allMightLikes: allMightLikes ?? null,
        lotteriesCategoryPages: pageContent ?? null,
      },
      revalidate: ISR_REVALIDATE,
    };
  } catch (error) {
    let message = '';
    if (error instanceof Error) message = error.message;
    else message = String(error);
    console.log('ERROR: ', message);
    return { props: { errors: message } };
  }
};

export default LotteriesSlug;
